import clsx from 'clsx'
import { ArrowRightIcon, ChevronDownIcon, EntityIcon } from '../SearchIcons'
import { SearchPopoverRow } from '../SearchPopoverParts'
import type { Row, SearchSelection, ToggleAction } from '../../logic/types'

function ResultMetaTrail({ row }: { row: Row }) {
  if (!row.metaTrail?.length) return null

  if (row.kind === 'pages') {
    return (
      <span className="ml-2 min-w-0 truncate text-[11px] font-normal text-muted">
        {row.metaTrail[0]}
      </span>
    )
  }

  return (
    <span className="ml-1 inline-flex min-w-0 items-center gap-1 text-[11px] font-normal text-muted">
      {row.metaTrail.map((part, index) => (
        <span key={`${row.key}:meta:${index}`} className="inline-flex min-w-0 items-center gap-1">
          <span className="text-[10px] leading-none">{index === 0 ? '@' : '>'}</span>
          <span className="truncate">{part}</span>
        </span>
      ))}
    </span>
  )
}

interface GlobalSearchResultRowProps {
  row: Row
  active: boolean
  onActivate: () => void
  onSelect: (selection: SearchSelection) => void
}

export function GlobalSearchResultRow({
  row,
  active,
  onActivate,
  onSelect,
}: GlobalSearchResultRowProps) {
  return (
    <SearchPopoverRow
      active={active}
      label={row.label}
      meta={<ResultMetaTrail row={row} />}
      leading={<EntityIcon kind={row.kind} className="mr-2 shrink-0 text-muted" />}
      trailing={
        <ArrowRightIcon
          className={clsx(
            'shrink-0 text-subtle transition-opacity',
            active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          )}
        />
      }
      onPointerEnter={onActivate}
      onClick={() => onSelect(row.selection)}
    />
  )
}

/** A result tab's row that hasn't loaded yet. */
export function GlobalSearchPlaceholderRow({
  active,
  onActivate,
}: {
  active: boolean
  onActivate: () => void
}) {
  return (
    <div
      data-active={active ? 'true' : undefined}
      className={clsx(
        'flex h-7 w-full items-center gap-2 rounded px-3 transition-colors hover:bg-[color:color-mix(in_srgb,var(--bg-muted)_22%,var(--surface-default))]',
        active && 'bg-[color:color-mix(in_srgb,var(--bg-muted)_22%,var(--surface-default))]'
      )}
      onPointerEnter={onActivate}
    >
      <span className="mr-0.5 h-3 w-3 shrink-0 rounded-sm bg-[color:var(--bg-muted)] opacity-80" />
      <span className="h-2.5 w-28 rounded bg-[color:var(--bg-muted)]" />
      <span className="h-2.5 w-16 rounded bg-[color:var(--bg-muted)] opacity-70" />
    </div>
  )
}

interface GlobalSearchSectionToggleProps {
  action: ToggleAction
  /** Whether the section's rows are expanded ("See more" was pressed). */
  isExpanded: boolean
  active: boolean
  onActivate: () => void
  onToggle: () => void
}

/** "See more" / "See less" under an All-tab section. */
export function GlobalSearchSectionToggle({
  action,
  isExpanded,
  active,
  onActivate,
  onToggle,
}: GlobalSearchSectionToggleProps) {
  const shouldSeeMore = action === 'more' || (action === 'toggle' && !isExpanded)
  const label = shouldSeeMore ? 'See more' : 'See less'

  return (
    <button
      type="button"
      data-active={active ? 'true' : undefined}
      onPointerEnter={onActivate}
      onClick={onToggle}
      className={clsx(
        'mt-0.5 flex h-7 w-full items-center gap-1 rounded px-3 text-left text-[11px] font-medium text-[color:var(--accent-info)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--bg-muted)_34%,var(--surface-default))] focus:outline-none',
        active && 'bg-[color:color-mix(in_srgb,var(--bg-muted)_34%,var(--surface-default))]'
      )}
      aria-expanded={isExpanded}
    >
      <span>{label}</span>
      <ChevronDownIcon
        className={clsx(
          'transition-transform',
          (action === 'less' || (action === 'toggle' && isExpanded)) && 'rotate-180'
        )}
      />
    </button>
  )
}
